import { useMemo } from 'react';
import { getBezierPath, getSmoothStepPath, getStraightPath, Position, type EdgeProps } from 'reactflow';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useUserWorkflowRole } from '@/state/workflow.state';
import { FF_SWIPE_TO_DELETE_EDGE } from '@/consts/featureFlags';
import { EdgeTypePreference } from '@/types/auth.types';
import { getHandleColor, getHandleName } from '../Nodes/DynamicNode/HandlesUtils';
import { useFlowView } from './FlowContext';
import { useUserPreferences } from './hooks/useUserPreferences';

interface CustomEdgeProps extends EdgeProps {
  getDragState?: () => { isDragging: boolean; isModifierPressed: boolean };
}

const getEdgePath = (
  sourcePosition: Position,
  targetPosition: Position,
  userPrefEdgeType: EdgeTypePreference,
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number,
) => {
  if (userPrefEdgeType === 'bezier') {
    return getBezierPath({ sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition });
  }
  if (userPrefEdgeType === 'line') {
    return getStraightPath({ sourceX, sourceY, targetX, targetY });
  }
  // 'step' and any unexpected runtime value: legacy default was smooth-step, not straight.
  return getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    borderRadius: 16,
  });
};

export const CustomEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style,
  markerEnd,
  getDragState,
}: CustomEdgeProps) => {
  const flowView = useFlowView();
  const isFFSwipeToDeleteEdgeEnabled = useFeatureFlagEnabled(FF_SWIPE_TO_DELETE_EDGE);
  const { userPrefEdgeType } = useUserPreferences();

  const [edgePath] = useMemo(
    () => getEdgePath(sourcePosition, targetPosition, userPrefEdgeType, sourceX, sourceY, targetX, targetY),
    [sourcePosition, targetPosition, userPrefEdgeType, sourceX, sourceY, targetX, targetY],
  );

  const gradientId = `gradient-${id}`;

  const role = useUserWorkflowRole();
  const pointerEvents = role === 'guest' ? 'none' : 'auto';
  const cursor = role === 'guest' ? 'default' : 'pointer';

  const edge = flowView.getEdgeNullable(id);

  // Handle edge deletion on mouse enter when dragging with modifier keys
  const handleMouseEnter = (_event: React.MouseEvent) => {
    if (!isFFSwipeToDeleteEdgeEnabled) return;
    if (role === 'guest') return;
    if (!getDragState) return;

    // Get current drag state
    const { isDragging, isModifierPressed } = getDragState();

    // Only delete if we're actively dragging with modifier keys pressed
    if (isDragging && isModifierPressed) {
      flowView.deleteEdges([id]);
    }
  };

  const clickStyle: React.CSSProperties = {
    strokeWidth: 40,
    stroke: 'transparent',
    cursor: role === 'guest' ? 'default' : 'pointer', // Disable pointer for guests
    pointerEvents: role === 'guest' ? 'none' : 'auto',
  };

  const visiblePathStyle = useMemo(() => {
    const base: React.CSSProperties = { cursor, pointerEvents };
    if (!style) return base;
    const {
      opacity: _opacity,
      strokeWidth: _strokeWidth,
      ...rest
    } = style as React.CSSProperties & {
      strokeWidth?: number | string;
    };
    return { ...rest, ...base };
  }, [style, cursor, pointerEvents]);

  const sourceHandleType = edge ? flowView.nodeOutputType(edge.source, getHandleName(edge.sourceHandle)) : undefined;
  const targetHandleType = edge ? flowView.nodeInputType(edge.target, getHandleName(edge.targetHandle)) : undefined;

  return (
    <g onMouseEnter={handleMouseEnter}>
      <defs>
        <linearGradient
          id={gradientId}
          gradientUnits="userSpaceOnUse"
          x1={sourceX}
          y1={sourceY}
          x2={targetX}
          y2={targetY}
        >
          <stop offset="0%" stopColor={getHandleColor(sourceHandleType)} />
          <stop offset="100%" stopColor={getHandleColor(targetHandleType)} />
        </linearGradient>
      </defs>
      <path d={edgePath} style={clickStyle} fill="none" />

      <path
        id={id}
        className="custom-edge-visible"
        style={visiblePathStyle}
        d={edgePath}
        markerEnd={markerEnd}
        stroke={`url(#${gradientId})`}
        fill="none"
      />
    </g>
  );
};
